import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Card,
  CardContent
} from '@material-ui/core';
import { solicitudDetalleService } from '../../services';

const getTipoPerfilColor = (tipo) => {
  switch (tipo) {
    case 'CONSERVADOR':
      return '#4caf50';
    case 'MODERADO':
      return '#ff9800';
    case 'AGRESIVO':
      return '#f44336';
    default:
      return '#666';
  }
};

const getTipoPerfilDescripcion = (tipo) => {
  switch (tipo) {
    case 'CONSERVADOR':
      return 'Perfil Conservador: Prefiere inversiones de bajo riesgo con preservación del capital.';
    case 'MODERADO':
      return 'Perfil Moderado: Balance entre riesgo y rentabilidad, acepta variaciones moderadas.';
    case 'AGRESIVO':
      return 'Perfil Agresivo: Busca altas rentabilidades asumiendo mayores riesgos.';
    default:
      return '';
  }
};

const SeccionPerfilInversor = ({ solicitudId, tipo }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [preguntas, setPreguntas] = useState([]);
  const [respuestas, setRespuestas] = useState([]);

  const cargarDatos = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const getMethod = tipo === 'individuo'
        ? solicitudDetalleService.getPerfilInversorIndividuo
        : solicitudDetalleService.getPerfilInversorEmpresa;
      const response = await getMethod(solicitudId);
      if (response) {
        setPreguntas(response.preguntas || []);
        setRespuestas(response.respuestas || []);
      }
    } catch (err) {
      setError('Error al cargar el perfil inversor');
    } finally {
      setLoading(false);
    }
  }, [solicitudId, tipo]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  const handleRespuestaChange = (preguntaId, opcionId) => {
    setRespuestas((prev) => {
      const restantes = prev.filter((resp) => resp.preguntaId !== preguntaId);
      return [...restantes, { preguntaId, opcionId }];
    });
  };

  const getRespuestaSeleccionada = (preguntaId) => {
    const respuesta = respuestas.find((resp) => resp.preguntaId === preguntaId);
    return respuesta ? respuesta.opcionId.toString() : '';
  };

  const calcularTipoPerfil = () => {
    for (const respuesta of respuestas) {
      const pregunta = preguntas.find((p) => p.id === respuesta.preguntaId);
      if (!pregunta) continue;
      const opcion = pregunta.opciones.find((o) => o.id === respuesta.opcionId);
      if (opcion && opcion.determinante && opcion.tipoPerfil) {
        return opcion.tipoPerfil;
      }
    }

    let puntajeTotal = 0;
    for (const respuesta of respuestas) {
      const pregunta = preguntas.find((p) => p.id === respuesta.preguntaId);
      if (!pregunta) continue;
      const opcion = pregunta.opciones.find((o) => o.id === respuesta.opcionId);
      if (opcion) {
        puntajeTotal += opcion.puntaje || 0;
      }
    }

    if (puntajeTotal >= 1 && puntajeTotal < 37) return 'CONSERVADOR';
    if (puntajeTotal >= 37 && puntajeTotal < 55) return 'MODERADO';
    if (puntajeTotal >= 55 && puntajeTotal < 90) return 'AGRESIVO';
    return null;
  };

  const isFormValid = () => {
    const habilitadas = preguntas.filter((pregunta) => pregunta.habilitada);
    return habilitadas.every((pregunta) =>
      respuestas.some((respuesta) => respuesta.preguntaId === pregunta.id)
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    if (!isFormValid()) {
      setError('Por favor responda todas las preguntas para continuar.');
      setSaving(false);
      return;
    }

    try {
      const datosParaGuardar = {
        solicitudId: parseInt(solicitudId),
        respuestas,
        tipoPerfil: calcularTipoPerfil()
      };
      const saveMethod = tipo === 'individuo'
        ? solicitudDetalleService.savePerfilInversorIndividuo
        : solicitudDetalleService.savePerfilInversorEmpresa;
      await saveMethod(datosParaGuardar);
      setSuccess('Datos guardados exitosamente');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Error al guardar los datos');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  const tipoPerfilCalculado = isFormValid() ? calcularTipoPerfil() : null;

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Perfil del Inversor
        </Typography>
        <Typography variant="body1" style={{ marginBottom: '2rem' }}>
          Para poder ofrecerte las mejores opciones de inversión, necesitamos conocer tu perfil como inversor.
          Por favor, responde las siguientes preguntas:
        </Typography>
        {error && (
          <Box p={2} mb={2} style={{ backgroundColor: '#ffebee', color: '#c62828', borderRadius: '4px' }}>
            <Typography variant="body2">{error}</Typography>
          </Box>
        )}
        {success && (
          <Box p={2} mb={2} style={{ backgroundColor: '#e8f5e9', color: '#2e7d32', borderRadius: '4px' }}>
            <Typography variant="body2">{success}</Typography>
          </Box>
        )}
        <form onSubmit={handleSubmit}>
          {preguntas
            .filter((pregunta) => pregunta.habilitada)
            .map((pregunta) => (
              <Card
                key={pregunta.id}
                style={{
                  marginBottom: '2rem',
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #e9ecef'
                }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    style={{
                      marginBottom: '1.5rem',
                      fontWeight: 'bold'
                    }}
                  >
                    {pregunta.pregunta}
                  </Typography>

                  <Box display="flex" flexDirection="column" gap={1} alignItems="stretch">
                    {pregunta.opciones && pregunta.opciones.map((opcion) => {
                      const seleccionada = getRespuestaSeleccionada(pregunta.id) === opcion.id.toString();
                      return (
                        <Button
                          key={opcion.id}
                          variant={seleccionada ? 'contained' : 'outlined'}
                          onClick={() => handleRespuestaChange(pregunta.id, opcion.id)}
                          style={{
                            margin: '4px 0',
                            width: '100%',
                            padding: '12px 16px',
                            fontSize: '14px',
                            backgroundColor: seleccionada ? '#2196f3' : 'transparent',
                            color: seleccionada ? 'white' : 'black',
                            borderColor: '#2196f3',
                            textTransform: 'none',
                            whiteSpace: 'normal',
                            height: 'auto',
                            minHeight: '48px',
                            justifyContent: 'flex-start',
                            textAlign: 'left'
                          }}
                        >
                          {opcion.valor}
                        </Button>
                      );
                    })}
                  </Box>
                </CardContent>
              </Card>
            ))}

          {tipoPerfilCalculado && (
            <Card
              style={{
                marginBottom: '2rem',
                backgroundColor: '#e8f5e8',
                border: `2px solid ${getTipoPerfilColor(tipoPerfilCalculado)}`,
                borderRadius: '8px'
              }}
            >
              <CardContent style={{ textAlign: 'center', padding: '2rem' }}>
                <Typography
                  variant="h5"
                  style={{
                    color: getTipoPerfilColor(tipoPerfilCalculado),
                    fontWeight: 'bold',
                    marginBottom: '1rem'
                  }}
                >
                  Calificación del Inversor
                </Typography>

                <Typography
                  variant="h4"
                  style={{
                    color: getTipoPerfilColor(tipoPerfilCalculado),
                    fontWeight: 'bold',
                    marginBottom: '1rem'
                  }}
                >
                  {tipoPerfilCalculado}
                </Typography>

                <Typography
                  variant="body1"
                  style={{
                    color: '#666',
                    maxWidth: '500px',
                    margin: '0 auto'
                  }}
                >
                  {getTipoPerfilDescripcion(tipoPerfilCalculado)}
                </Typography>
              </CardContent>
            </Card>
          )}

          <Box marginTop={2}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={saving || !isFormValid()}
              fullWidth
            >
              {saving ? <CircularProgress size={20} /> : 'Guardar'}
            </Button>
          </Box>
        </form>
      </CardContent>
    </Card>
  );
};

export default SeccionPerfilInversor;

